import { createSelector } from 'redux-bundler';
import { queryFromObject } from '@src/utils';
import { ApiStatuses } from '@src/utils/enums';
import { db } from '@src/app-pages/data-entry/offline/db';
import { isOfflineSiteRouteKey } from '@src/app-pages/data-entry/offline/site-route-key';

const getSiteRouteKey = (params) =>
  String(params?.siteId ?? params?.site_id ?? params?.siteFid ?? params?.site_fid ?? '');

const sitesDatasheetBundle = {
  name: 'sitesDatasheet',

  getReducer: () => {
    const initialData = {
      pageSize: 50,
      pageNumber: 0,
      params: {},
      siteRouteKey: '',
      loadId: 0,
      missouriRiver: {
        data: [],
        totalCount: 0,
      },
      searchEffort: {
        data: [],
        totalCount: 0,
      },
    };

    return (state = initialData, { type, payload }) => {
      switch (type) {
        case 'UPDATE_SITES_DATASHEET_PARAMS':
          return {
            ...state,
            params: payload,
            ...(getSiteRouteKey(payload) !== state.siteRouteKey
              ? {
                  siteRouteKey: getSiteRouteKey(payload),
                  loadId: state.loadId + 1,
                  missouriRiver: { data: [], totalCount: 0 },
                  searchEffort: { data: [], totalCount: 0 },
                }
              : {}),
          };
        case 'SITES_DATASHEET_LOAD_STARTED':
          return {
            ...state,
            siteRouteKey: payload.siteRouteKey,
            loadId: state.loadId + 1,
            ...(payload.siteRouteKey !== state.siteRouteKey
              ? {
                  missouriRiver: { data: [], totalCount: 0 },
                  searchEffort: { data: [], totalCount: 0 },
                }
              : {}),
          };
        case 'SET_SITES_DATASHEET_PAGINATION':
          return {
            ...state,
            pageSize: payload.pageSize,
            pageNumber: payload.pageNumber,
          };
        case 'UPDATE_MORIVER_SITES_DATASHEET':
          if (payload.loadId !== state.loadId) return state;
          return {
            ...state,
            missouriRiver: {
              data: payload.items ?? [],
              totalCount: payload.items?.length ?? 0,
            },
          };
        case 'UPDATE_SEARCH_EFFORT_SITES_DATASHEET':
          if (payload.loadId !== state.loadId) return state;
          return {
            ...state,
            searchEffort: {
              data: payload.items ?? [],
              totalCount: payload.items?.length ?? 0,
            },
          };
        default:
          return state;
      }
    };
  },

  selectSitesDatasheet: (state) => state.sitesDatasheet,
  selectSitesDatasheetPageSize: (state) => state.sitesDatasheet.pageSize,
  selectSitesDatasheetPageNumber: (state) => state.sitesDatasheet.pageNumber,
  selectSitesDatasheetParams: (state) => state.sitesDatasheet.params,

  selectCurrentSiteDatasheet: createSelector(
    'selectSitesDatasheet',
    'selectRouteParams',
    (datasheet, routeParams) =>
      datasheet.siteRouteKey === String(routeParams?.siteId ?? '') ? datasheet : null
  ),

  // Missouri River Data
  selectMoriverDraftSitesDatasheetData: createSelector('selectCurrentSiteDatasheet', (datasheet) =>
    (datasheet?.missouriRiver.data ?? []).filter((row) => row.status === 1)
  ),
  selectMoriverSitesDatasheetData: createSelector('selectCurrentSiteDatasheet', (datasheet) =>
    (datasheet?.missouriRiver.data ?? []).filter((row) => row.status === 2)
  ),
  selectMoriverSitesDraftDatasheetTotalResults: createSelector(
    'selectMoriverDraftSitesDatasheetData',
    (data) => data.length
  ),
  selectMoriverSitesDatasheetTotalResults: createSelector('selectMoriverSitesDatasheetData', (data) => data.length),

  // Search Effort Data
  selectSearchEffortSitesDraftDatasheetData: createSelector('selectCurrentSiteDatasheet', (datasheet) =>
    (datasheet?.searchEffort.data ?? []).filter((row) => row.status === 1)
  ),
  selectSearchEffortSitesDatasheetData: createSelector('selectCurrentSiteDatasheet', (datasheet) =>
    (datasheet?.searchEffort.data ?? []).filter((row) => row.status === 2)
  ),
  selectSearchEffortSitesDatasheetTotalResults: createSelector(
    'selectSearchEffortSitesDatasheetData',
    (data) => data.length
  ),
  selectSearchEffortSitesDraftDatasheetTotalResults: createSelector(
    'selectSearchEffortSitesDraftDatasheetData',
    (data) => data.length
  ),

  doSitesDatasheetLoadData:
    (siteRouteKeyFromPage = null) =>
    async ({ dispatch, store }) => {
      const params = store.selectSitesDatasheetParams();
      const siteRouteKey = String(siteRouteKeyFromPage ?? getSiteRouteKey(params));
      dispatch({ type: 'SITES_DATASHEET_LOAD_STARTED', payload: { siteRouteKey } });
      const { loadId } = store.selectSitesDatasheet();
      if (navigator.onLine && !isOfflineSiteRouteKey(siteRouteKey)) {
        store.doFetchMoRiverSitesDatasheets(loadId);
        store.doFetchSearchEffortSitesDatasheets(loadId);
        return;
      }
      const moriverData = await db.moriver.toArray();
      const searchData = await db.search.toArray();
      const fishData = await db.fish.toArray();
      const telemetryData = await db.telemetry.toArray();
      const supplementalData = await db.supplemental.toArray();
      const procedureData = await db.procedure.toArray();

      const matchCurrentSite = (row) => {
        const rowSiteKey = [row?.siteRouteKey, row?.siteFid, row?.site_fid, row?.siteId, row?.site_id];
        return rowSiteKey.some(
          (value) => value !== undefined && value !== null && String(value) === String(siteRouteKey)
        );
      };

      // Populate Sites Datasheet Missouri River Table with offline children counts
      const siteMoriverData = moriverData.filter(matchCurrentSite).map((moriverRow) => {
        const moriverIds = [moriverRow?.mrId, moriverRow?.mr_id, moriverRow?.mrFid, moriverRow?.mr_fid]
          .filter((value) => value !== undefined && value !== null)
          .map(String);

        const matchedMoriver = (row) => {
          const childMoriverIds = [row?.mrId, row?.mr_id, row?.mrFid, row?.mr_fid]
            .filter((value) => value !== undefined && value !== null)
            .map(String);

          return childMoriverIds.some((value) => moriverIds.includes(value));
        };
        const fishCount = fishData.filter(matchedMoriver).length;
        const suppCount = supplementalData.filter(matchedMoriver).length;
        const procCount = procedureData.filter(matchedMoriver).length;

        return {
          ...moriverRow,
          fishCount: Math.max(Number(moriverRow?.fishCount ?? 0), fishCount),
          suppCount: Math.max(Number(moriverRow?.suppCount ?? 0), suppCount),
          procCount: Math.max(Number(moriverRow?.procCount ?? 0), procCount),
        };
      });

      // Populate Sites Datasheet Search Effort Table with offline children counts
      const siteSearchData = searchData.filter(matchCurrentSite).map((searchRow) => {
        const searchIds = [searchRow?.seId, searchRow?.se_id, searchRow?.seFid, searchRow?.se_fid]
          .filter((value) => value !== undefined && value !== null)
          .map(String);

        const matchedSearch = (row) => {
          const childSearchIds = [row?.seId, row?.se_id, row?.seFid, row?.se_fid]
            .filter((value) => value !== undefined && value !== null)
            .map(String);

          return childSearchIds.some((value) => searchIds.includes(value));
        };

        const telemetryCount = telemetryData.filter(matchedSearch).length;

        return {
          ...searchRow,
          telemetryCount: Math.max(Number(searchRow?.telemetryCount ?? 0), telemetryCount),
        };
      });

      dispatch({
        type: 'UPDATE_MORIVER_SITES_DATASHEET',
        payload: {
          loadId,
          items: siteMoriverData,
          totalCount: siteMoriverData.length,
        },
      });

      dispatch({
        type: 'UPDATE_SEARCH_EFFORT_SITES_DATASHEET',
        payload: {
          loadId,
          items: siteSearchData,
          totalCount: siteSearchData.length,
        },
      });
    },

  doFetchMoRiverSitesDatasheets:
    (loadId) =>
    ({ dispatch, store, apiGet }) => {
      const { ...params } = store.selectSitesDatasheetParams();
      const size = store.selectSitesDatasheetPageSize();
      const number = store.selectSitesDatasheetPageNumber();

      const query = queryFromObject({
        ...params,
        size,
        number,
      });

      const url = `/psapi/missouriDatasheets${query}`;

      apiGet(url, (err, body) => {
        if (!err && body?.status === ApiStatuses.Success) {
          dispatch({ type: 'UPDATE_MORIVER_SITES_DATASHEET', payload: { ...body?.data, loadId } });
        } else {
          dispatch({
            type: 'MORIVER_SITES_DATASHEETS_FETCH_ERROR',
            payload: err,
          });
        }
      });
    },

  doFetchSearchEffortSitesDatasheets:
    (loadId) =>
    ({ dispatch, store, apiGet }) => {
      const { ...params } = store.selectSitesDatasheetParams();
      const size = store.selectSitesDatasheetPageSize();
      const number = store.selectSitesDatasheetPageNumber();

      const query = queryFromObject({
        ...params,
        size,
        number,
      });

      const url = `/psapi/searchDatasheets${query}`;

      apiGet(url, (err, body) => {
        if (!err && body?.status === ApiStatuses.Success) {
          dispatch({
            type: 'UPDATE_SEARCH_EFFORT_SITES_DATASHEET',
            payload: { ...body?.data, loadId },
          });
        } else {
          dispatch({
            type: 'SEARCH_EFFORT_SITES_DATASHEETS_FETCH_ERROR',
            payload: err,
          });
        }
      });
    },

  doSetSitesDatasheetPagination:
    ({ pageSize, pageNumber }) =>
    ({ dispatch, store }) => {
      dispatch({
        type: 'SET_SITES_DATASHEET_PAGINATION',
        payload: { pageSize, pageNumber },
      });
    },

  doUpdateSitesDatasheetParams:
    (params) =>
    ({ dispatch, store }) => {
      dispatch({ type: 'UPDATE_SITES_DATASHEET_PARAMS', payload: params });
    },
};

export default sitesDatasheetBundle;
